import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface CreamPercentageData {
  cycleNum: number;
  formData: {
    product: string;
    machineNo: string;
    line: string;
    standardCreamPercentage: string;
    executiveName?: string;
  };
  weightData: {
    sandwichWeights: string[];
    shellWeights: string[];
  };
  qualityTourId: string;
  userName: string | null;
  shiftValue: string;
  timestamp: string;
}

interface CreamPercentageState {
  cycleData: { [key: number]: CreamPercentageData[] }; // Multiple products per cycle
  completedCycles: number[];
  currentCycle: number;
  isOffline: boolean;
  pendingSync: CreamPercentageData[];
}

const initialState: CreamPercentageState = {
  cycleData: {},
  completedCycles: [],
  currentCycle: 1,
  isOffline: false,
  pendingSync: []
};

const creamPercentageSlice = createSlice({
  name: 'creamPercentage',
  initialState,
  reducers: {
    saveCycleData: (state, action: PayloadAction<CreamPercentageData & { forceOffline?: boolean }>) => {
      const { forceOffline, ...rest } = action.payload;
      const payload = { ...rest, timestamp: new Date().toISOString() };
      const { cycleNum } = payload;

      if (!state.cycleData[cycleNum]) {
        state.cycleData[cycleNum] = [];
      }
      state.cycleData[cycleNum].push(payload);

      if (!state.completedCycles.includes(cycleNum)) {
        state.completedCycles.push(cycleNum);
      }

      const shouldAddToPendingSync = state.isOffline || forceOffline;

      if (shouldAddToPendingSync) {
        state.pendingSync.push(payload);
      }
    },

    completeCycle: (state, action: PayloadAction<number>) => {
      const cycleNum = action.payload;
      state.currentCycle = Math.min(cycleNum + 1, 9);
    },
    
    setOfflineMode: (state, action: PayloadAction<boolean>) => {
      state.isOffline = action.payload;
    },
    
    loadOfflineData: (state, action: PayloadAction<{
      cycleData: { [key: number]: CreamPercentageData | CreamPercentageData[] };
      completedCycles: number[];
      currentCycle: number;
    }>) => {
      const raw = action.payload.cycleData;
      state.cycleData = {};
      Object.entries(raw || {}).forEach(([k, v]) => {
        const cycleNum = parseInt(k);
        state.cycleData[cycleNum] = Array.isArray(v) ? v : [v];
      });
      state.completedCycles = action.payload.completedCycles;
      state.currentCycle = action.payload.currentCycle;
    },
    
    clearPendingSync: (state) => {
      state.pendingSync = [];
    },
    
    removePendingSyncItem: (state, action: PayloadAction<number>) => {
      const indexToRemove = action.payload;
      if (typeof indexToRemove === 'number' && indexToRemove >= 0 && indexToRemove < state.pendingSync.length) {
        state.pendingSync.splice(indexToRemove, 1);
      }
    },
    
    resetCreamPercentage: (state) => {
      state.cycleData = {};
      state.completedCycles = [];
      state.currentCycle = 1;
      state.pendingSync = [];
      state.isOffline = false;
    }
  }
});

export const {
  saveCycleData,
  completeCycle,
  setOfflineMode,
  loadOfflineData,
  clearPendingSync,
  removePendingSyncItem,
  resetCreamPercentage
} = creamPercentageSlice.actions;

export default creamPercentageSlice.reducer;
